import React from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { Input } from "../Input";
import { DeviceInfo } from "../../services/vision/types";
import {
  ResultItem,
  ResultLabel,
  ResultValue,
  EditableResultValue,
  ActionButton,
  DeleteButton,
  RequiredIndicator,
  DeviceTypeContainer,
} from "./styles";
import { formatFieldValue, formatOSInfo, getFieldLabel } from "./utils";

interface FieldItemProps {
  fieldKey: string;
  value: any;
  deviceInfo: DeviceInfo;
  editingField: string | null;
  isRequired?: boolean;
  fieldLabels: Record<string, string>;
  errors: Record<string, string>;
  onStartEditing: (field: string) => void;
  onSaveField: (field: string) => void;
  onCancelEditing: () => void;
  onChange: (field: string, value: string) => void;
  onDelete: (field: string, isCustomField?: boolean) => void;
  isCustomField?: boolean;
}

export const FieldItem: React.FC<FieldItemProps> = ({
  fieldKey,
  value,
  deviceInfo,
  editingField,
  isRequired = false,
  fieldLabels,
  errors,
  onStartEditing,
  onSaveField,
  onCancelEditing,
  onChange,
  onDelete,
  isCustomField = false,
}) => {
  const isCurrentlyEditing = editingField === fieldKey;
  const canEdit = fieldKey !== "title" || !isRequired;

  return (
    <ResultItem key={fieldKey}>
      <ResultLabel $isRequired={isRequired}>
        {getFieldLabel(fieldKey, fieldLabels)}
        {isRequired && <RequiredIndicator>*</RequiredIndicator>}
      </ResultLabel>

      {isCurrentlyEditing ? (
        <EditableResultValue>
          <Input
            value={(value as string) || ""}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSaveField(fieldKey);
              }
            }}
            placeholder={`Enter ${getFieldLabel(
              fieldKey,
              fieldLabels
            ).toLowerCase()}`}
            error={errors[fieldKey]}
            autoFocus
          />
          <ActionButton onClick={() => onSaveField(fieldKey)} title="Save">
            <FiCheck size={18} color="green" />
          </ActionButton>
          <ActionButton onClick={onCancelEditing} title="Cancel">
            <FiX size={18} color="red" />
          </ActionButton>
        </EditableResultValue>
      ) : (
        <ResultValue
          $editable={true}
          $isEmpty={!value || value === ""}
          onClick={() => onStartEditing(fieldKey)}
        >
          {fieldKey === "type" ? (
            <DeviceTypeContainer>
              {formatFieldValue(fieldKey, value, deviceInfo)}
            </DeviceTypeContainer>
          ) : fieldKey === "osName" ? (
            formatOSInfo(deviceInfo.osName, deviceInfo.osVersion)
          ) : (
            formatFieldValue(fieldKey, value, deviceInfo)
          )}
        </ResultValue>
      )}

      {/* Delete button */}
      {canEdit && !isCurrentlyEditing && (
        <DeleteButton
          onClick={() => onDelete(fieldKey, isCustomField)}
          title="Delete"
        >
          <FiX size={16} />
        </DeleteButton>
      )}
    </ResultItem>
  );
};
